/**
 * Iter 87 - Portail Artisan / Entreprises Travaux (Fev 2026).
 *
 * Vue dediee aux artisans / entreprises travaux. Acces limite :
 * - Liste des projets sur lesquels ils ont une intervention (identifies par leur email)
 * - Detail projet : delais (read-only) + planning + leurs interventions
 * - Mise a jour status + dates de leurs interventions (auto-save)
 * - Upload de documents (factures, photos chantier, attestations)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  planifie: 'Planifie',
  en_cours: 'En cours',
  termine: 'Termine',
};
const STATUS_BG = {
  planifie: 'bg-gray-100 text-gray-800',
  en_cours: 'bg-amber-100 text-amber-800',
  termine: 'bg-emerald-100 text-emerald-800',
};

const fmtEUR = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('fr-FR')} EUR`);
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('fr-FR'); } catch { return s; }
};

const ArtisanPortalPage = ({ user, onLogout }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/artisan/projects`);
      setProjects(r.data.projects || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  const loadProject = useCallback(async (pid) => {
    setLoading(true);
    try {
      const [r, rd] = await Promise.all([
        axios.get(`${API}/artisan/projects/${pid}`),
        axios.get(`${API}/artisan/projects/${pid}/documents`),
      ]);
      setProject(r.data);
      setDocs(rd.data.documents || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (projectId) loadProject(projectId);
    else loadProjects();
  }, [projectId, loadProject, loadProjects]);

  const updateIntervention = async (interventionId, patch) => {
    try {
      await axios.put(`${API}/artisan/interventions/${interventionId}`, patch);
      if (projectId) loadProject(projectId);
    } catch (e) {
      alert('Erreur mise a jour : ' + (e.response?.data?.detail || e.message));
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/artisan/projects/${projectId}/documents?category=general`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      e.target.value = '';
      loadProject(projectId);
    } catch (err) {
      alert('Erreur upload : ' + (err.response?.data?.detail || err.message));
    } finally { setUploading(false); }
  };

  // === LISTE PROJETS ===
  if (!projectId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-testid="artisan-header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#A57F4F] flex items-center justify-center text-white font-bold">RE</div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Portail Artisan</h1>
              <p className="text-xs text-gray-500">{user?.company_name || user?.name || user?.email}</p>
            </div>
          </div>
          <button onClick={onLogout} className="text-sm text-gray-600 hover:text-gray-900" data-testid="artisan-logout">Deconnexion</button>
        </header>

        <main className="max-w-5xl mx-auto px-6 py-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Mes projets</h2>
          <p className="text-sm text-gray-500 mb-6">Liste des projets sur lesquels vous avez ete invite a intervenir.</p>

          {loading && <p className="text-gray-400 italic">Chargement...</p>}
          {error && <p className="text-red-600 text-sm" data-testid="artisan-error">{error}</p>}
          {!loading && !error && projects.length === 0 && (
            <div className="realease-card p-12 text-center" data-testid="artisan-empty">
              <p className="text-gray-600">Aucun projet ne vous a encore ete attribue.</p>
              <p className="text-xs text-gray-400 mt-2">Si vous attendez une mission, contactez le responsable RealEase.</p>
            </div>
          )}
          {!loading && projects.length > 0 && (
            <div className="space-y-3" data-testid="artisan-projects-list">
              {projects.map((p) => (
                <button
                  key={p.project_id}
                  onClick={() => navigate(`/artisan/${p.project_id}`)}
                  className="block w-full text-left p-4 rounded-lg bg-white border border-gray-200 hover:border-[#A57F4F] hover:shadow-md transition"
                  data-testid={`artisan-project-${p.project_id}`}
                >
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{p.name}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{p.client_name} {p.city ? `· ${p.city}` : ''}</p>
                    </div>
                    <span className="text-xs font-semibold text-[#A57F4F] bg-[#A57F4F]/10 px-2 py-0.5 rounded">
                      {p.my_interventions_count ?? p.interventions_count} intervention(s)
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </main>
      </div>
    );
  }

  // === DETAIL PROJET ===
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/artisan')} className="text-[#A57F4F] hover:underline text-sm" data-testid="artisan-back">← Mes projets</button>
        </div>
        <button onClick={onLogout} className="text-sm text-gray-600 hover:text-gray-900">Deconnexion</button>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading && <p className="text-gray-400 italic">Chargement...</p>}
        {error && <p className="text-red-600 text-sm">{error}</p>}
        {project && (
          <>
            <h2 className="text-2xl font-bold text-gray-900" data-testid="artisan-project-name">{project.name}</h2>
            <p className="text-sm text-gray-500 mb-6">{project.client_name} {project.city ? `· ${project.city}` : ''}</p>

            {/* Mes interventions (avec couts visibles + edition) */}
            <section className="realease-card p-5 mb-6" data-testid="artisan-interventions-section">
              <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <span className="w-1 h-5 bg-[#A57F4F] rounded"></span>
                Mes interventions
              </h3>
              <p className="text-xs text-gray-500 mb-3">{project.my_interventions_count || 0} intervention(s) sur ce chantier · Vous pouvez modifier le statut et les dates.</p>
              {((project.interventions || []).filter((it) => it.mine)).length === 0 ? (
                <p className="text-gray-400 italic text-sm">Aucune intervention assignee.</p>
              ) : (
                <div className="space-y-3">
                  {((project.interventions || []).filter((it) => it.mine)).map((it) => {
                    const planning = (project.planning_items || []).find((pi) => pi.intervention_id === it.id) || {};
                    return (
                      <div key={it.id} className="p-3 rounded-lg border border-[#A57F4F]/30 bg-[#A57F4F]/5" data-testid={`artisan-intervention-${it.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                          <div>
                            <p className="font-semibold text-gray-900 flex items-center gap-1.5">
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#A57F4F] text-white uppercase">Mon intervention</span>
                              {it.intervenant}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">{it.description || '—'}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-base font-bold text-[#13736D]" data-testid={`artisan-intervention-cost-${it.id}`}>{fmtEUR(it.cost_ttc)} TTC</span>
                            <p className="text-[10px] text-gray-400">{fmtEUR(it.cost_ht)} HT</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                          <div>
                            <label className="text-[10px] uppercase text-gray-500 font-semibold block">Statut</label>
                            <select
                              value={planning.status || 'planifie'}
                              onChange={(e) => updateIntervention(it.id, { status: e.target.value })}
                              className="border border-gray-200 rounded px-2 py-1 text-sm w-full bg-white"
                              data-testid={`artisan-status-${it.id}`}
                            >
                              <option value="planifie">Planifie</option>
                              <option value="en_cours">En cours</option>
                              <option value="termine">Termine</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] uppercase text-gray-500 font-semibold block">Date debut</label>
                            <input
                              type="date"
                              key={`s-${it.id}-${(planning.start_date || '').slice(0,10)}`}
                              defaultValue={(planning.start_date || '').slice(0, 10)}
                              onBlur={(e) => {
                                const v = e.target.value || null;
                                if ((v || '') !== ((planning.start_date || '').slice(0,10))) updateIntervention(it.id, { start_date: v });
                              }}
                              className="border border-gray-200 rounded px-2 py-1 text-sm w-full bg-white"
                              data-testid={`artisan-start-${it.id}`}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase text-gray-500 font-semibold block">Date fin</label>
                            <input
                              type="date"
                              key={`e-${it.id}-${(planning.end_date || '').slice(0,10)}`}
                              defaultValue={(planning.end_date || '').slice(0, 10)}
                              onBlur={(e) => {
                                const v = e.target.value || null;
                                if ((v || '') !== ((planning.end_date || '').slice(0,10))) updateIntervention(it.id, { end_date: v });
                              }}
                              className="border border-gray-200 rounded px-2 py-1 text-sm w-full bg-white"
                              data-testid={`artisan-end-${it.id}`}
                            />
                          </div>
                        </div>
                        <div className="mt-1">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${STATUS_BG[planning.status] || STATUS_BG.planifie}`}>
                            {STATUS_LABELS[planning.status] || 'Planifie'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Iter 99 : Planning complet du chantier (toutes entreprises, couts masques) */}
            <section className="realease-card p-5 mb-6" data-testid="artisan-full-schedule-section">
              <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <span className="w-1 h-5 bg-[#13736D] rounded"></span>
                Planning du chantier
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Toutes les interventions du chantier (autres entreprises incluses) pour vous permettre de coordonner.
                Les montants ne sont visibles que pour vos propres interventions.
              </p>
              {(() => {
                const others = (project.interventions || []).filter((it) => !it.mine);
                if (others.length === 0) {
                  return <p className="text-gray-400 italic text-sm">Vous etes la seule entreprise pour le moment.</p>;
                }
                // Tri par date de debut
                const enriched = others.map((it) => {
                  const planning = (project.planning_items || []).find((pi) => pi.intervention_id === it.id) || {};
                  return { ...it, _planning: planning };
                });
                enriched.sort((a, b) => {
                  const da = (a._planning.start_date || a._planning.target_date || '9999');
                  const db_ = (b._planning.start_date || b._planning.target_date || '9999');
                  return da < db_ ? -1 : da > db_ ? 1 : 0;
                });
                return (
                  <div className="space-y-2">
                    {enriched.map((it) => {
                      const planning = it._planning;
                      return (
                        <div
                          key={it.id}
                          className="p-3 rounded-lg border border-gray-200 bg-gray-50/50 flex items-center justify-between gap-3 flex-wrap"
                          data-testid={`artisan-other-intervention-${it.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {it.works_phase && (
                                <span className="text-[9px] font-bold uppercase tracking-wider text-[#13736D] bg-white px-1.5 py-0.5 rounded border border-[#13736D]/20">{it.works_phase}</span>
                              )}
                              <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${STATUS_BG[planning.status] || STATUS_BG.planifie}`}>
                                {STATUS_LABELS[planning.status] || 'Planifie'}
                              </span>
                              <span className="text-sm font-semibold text-gray-800">{it.intervenant || it.description || 'Intervention'}</span>
                            </div>
                            {it.description && it.description !== it.intervenant && (
                              <p className="text-[11px] text-gray-500 mt-0.5 truncate">{it.description}</p>
                            )}
                          </div>
                          <div className="text-right text-xs text-gray-600">
                            <p className="font-semibold">
                              {planning.start_date && planning.end_date
                                ? `Du ${fmtDate(planning.start_date)} au ${fmtDate(planning.end_date)}`
                                : fmtDate(planning.start_date || planning.target_date)}
                            </p>
                            <p className="text-[10px] text-gray-400 italic">Montant masque</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </section>

            {/* Delais projet (read-only) */}
            <section className="realease-card p-5 mb-6" data-testid="artisan-delays-section">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-1 h-5 bg-gray-500 rounded"></span>
                Delais et echeances projet
              </h3>
              {(project.delays || []).length === 0 ? (
                <p className="text-gray-400 italic text-sm">Aucun delai partage.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {(project.delays || []).map((d) => (
                    <li key={d.delay_id} className="flex items-center justify-between p-2 rounded bg-gray-50">
                      <span className="text-gray-700">{d.description}</span>
                      <span className="text-xs text-gray-500">{fmtDate(d.target_date)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Documents uploads */}
            <section className="realease-card p-5" data-testid="artisan-docs-section">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-1 h-5 bg-[#13736D] rounded"></span>
                Mes documents (factures, photos, attestations)
              </h3>
              <label className="inline-block">
                <input type="file" onChange={uploadFile} disabled={uploading} className="hidden" data-testid="artisan-upload-input" />
                <span className="px-4 py-2 rounded-md bg-[#A57F4F] text-white text-sm font-semibold cursor-pointer hover:bg-[#8d6a40] inline-block" data-testid="artisan-upload-btn">
                  {uploading ? 'Envoi en cours...' : '+ Deposer un fichier'}
                </span>
              </label>
              {docs.length === 0 ? (
                <p className="text-gray-400 italic text-sm mt-3">Aucun document depose.</p>
              ) : (
                <ul className="space-y-1.5 mt-3 text-sm" data-testid="artisan-docs-list">
                  {docs.map((d) => (
                    <li key={d.document_id} className="flex items-center justify-between p-2 rounded bg-gray-50">
                      <div>
                        <p className="font-medium text-gray-800">{d.filename}</p>
                        <p className="text-xs text-gray-500">{fmtDate(d.uploaded_at)} · {(d.size_bytes / 1024).toFixed(1)} ko</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default ArtisanPortalPage;
